import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useState, useMemo } from 'react';
import { Pill } from '../../../../components/shared/Pill';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { StoreItemModel } from '../../../../app/services/api/orderManagementClient';
import { Section } from '../../../../components/ui/Section';
import { ContentCopy } from '@mui/icons-material';

interface StoreItemTableProps {
  readonly list: StoreItemModel[];
  readonly isLoading: boolean;
  readonly hasWritePermissions: boolean;
  readonly handleItemChange: (item: StoreItemModel) => void;
  readonly handleSetQuantityForAll?: (itemId: number, quantity: number) => void;
}

const StoreItemTable: React.FC<StoreItemTableProps> = ({
  list,
  isLoading,
  hasWritePermissions,
  handleItemChange,
  handleSetQuantityForAll,
}) => {
  const { t } = useTranslation('autoshipment');
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 10;

  // Filter and paginate
  const filteredAndPaginatedList = useMemo(() => {
    // Filter by search term
    const filtered = list.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.itemNumber?.toLowerCase().includes(searchLower) ||
        item.itemDescription?.toLowerCase().includes(searchLower) ||
        item.itemUom?.toLowerCase().includes(searchLower) ||
        item.storeNumber?.toLowerCase().includes(searchLower) ||
        item.storeName?.toLowerCase().includes(searchLower)
      );
    });

    // Paginate
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      items: filtered.slice(startIndex, endIndex),
      totalItems: filtered.length,
    };
  }, [list, searchTerm, page]);

  const maxPages = Math.ceil(filteredAndPaginatedList.totalItems / pageSize);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleCopyQuantityToAll = (item: StoreItemModel) => {
    if (handleSetQuantityForAll && item.itemId && item.quantity != null) {
      handleSetQuantityForAll(item.itemId, item.quantity);
    }
  };

  return (
    <>
      <Grid container
        sx={{ pb: '12px' }}
        spacing={4}>
        {hasWritePermissions && <Grid item
          xs={12}
          sm={6}
          md={3}>
          <TextField
            fullWidth
            size='small'
            label={t('search')}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </Grid>}
      </Grid>
      <Section>
        <TableHeader>
          {t('itemSelection')}
          {filteredAndPaginatedList.items.length && <Pill text={`${filteredAndPaginatedList.totalItems} item${filteredAndPaginatedList.items.length === 1 ? '' : 's'}`}></Pill>}
        </TableHeader>
        {isLoading ? <TableLoading headerHeight={45}
          bodyRowHeight={40}
          minTableWidth={480} /> :
          <TableContainer>
            <Table sx={{ minWidth: '480px' }}>
              <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('itemNumber')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('itemDescription')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('uom')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('quantity')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('lotNumber')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('storeName')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndPaginatedList.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}
                      align="center">
                      <Typography variant="textSM"
                        color="text.secondary"
                        sx={{ py: 4 }}>
                        {searchTerm ? t('noResultsFound') : t('noItemsAvailable')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndPaginatedList.items.map((row: StoreItemModel, index: number) => (
                    <TableRow key={`${row.storeNumber}-${row.itemNumber}-${index}`}>
                      <TableCell sx={{ width: 200 }}>
                        <Typography variant='textXS'>
                          {row.itemNumber ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 200 }}>
                        <Typography variant='textXS'>
                          {row.itemDescription ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        <Typography variant='textXS'>
                          {row.itemUom ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 150 }}>
                        <TextField
                          disabled={!hasWritePermissions}
                          key={`qty-${row.storeNumber}-${row.itemNumber}-${row.quantity}`}
                          type="number"
                          defaultValue={row.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (e.target.value != '') {
                              const nr = Number(value);
                              if (isNaN(nr) || nr <= 0) {
                                e.target.value = row.quantity?.toString() ?? '';
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const newQuantity = e.target.value;
                            if (newQuantity !== row.quantity?.toString()) {
                              const nr = newQuantity == '' ? null : Number(newQuantity);
                              const updatedRow = { ...row, quantity: nr };
                              handleItemChange(updatedRow);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 150 }}>
                        <TextField
                          disabled={!hasWritePermissions}
                          key={`lot-${row.storeNumber}-${row.itemNumber}-${row.lotNumber}`}
                          defaultValue={row.lotNumber}
                          onBlur={(e) => {
                            const newLotNumber = e.target.value;
                            if (newLotNumber !== row.lotNumber?.toString()) {
                              const updatedRow = { ...row, lotNumber: newLotNumber };
                              handleItemChange(updatedRow);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 200 }}>
                        <Typography variant='textXS'>
                          {row.storeName ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center"
                        sx={{ width: 20 }}>
                        <Tooltip title={t('setQuantityForAllStores')}>
                          <span>
                            <IconButton aria-label={t('setQuantityForAllStores')}
                              size="small"
                              onClick={() => handleCopyQuantityToAll(row)}
                              disabled={row.quantity == null || row.quantity === 0 || !hasWritePermissions}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>}
        <CustomPagination
          useNoDataMessage={false}
          page={page}
          setPage={setPage}
          maxPages={maxPages}
          breakpointForChangeDisplay={120000}
          disabled={isLoading} />
      </Section>
    </>
  );
};

export default StoreItemTable;